import { useCallback, useMemo, useState } from "react";
import {
  AMC_AUDIENCE_STATUSES,
  getTemplateSpecById,
  latestSelectableAudienceDay,
  MARKET_PLACE_ID_TO_COUNTRY_CODE_MAP,
  storedCalendarDay,
} from "../../../modules/amc/utils/utils";
import { useAmcAdvertisers } from "../../../modules/amc/api/useAmcAdvertisers";
import { useMarketplaceLabel } from "../../../utils/marketplaceConstants";
import { CustomTriggerTooltip } from "../../../components/core/basic/Tooltip";
import { Col, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { useAppSelector } from "../../../redux/hooks";
import { toTitleCase } from "../../../utils/formatText";
import { useFetch } from "../../../hooks/api";
import { listFormatter } from "../../../utils/listFormatter";
import { css } from "../../../../styled-system/css";
import Card from "../../../components/core/basic/Card";
import { formatCurrency, formatNumber } from "../../../utils/formatNumber";
import AudienceForm from "../../../modules/amc/components/audience/AudienceForm";
import { FaClone } from "react-icons/fa6";
import { Button } from "../../../components/core/basic/Button";
import { useAudienceTemplates } from "../../../modules/amc/components/audience/useAudienceTemplates";
import { Flex, Grid, HStack, VStack } from "../../../../styled-system/jsx";
import { FaInfoCircle } from "react-icons/fa";
import { MdRotateLeft } from "react-icons/md";
import { successToast } from "../../../utils/toasts";
import useQueryParams from "../../../hooks/useQueryParams";
import { ButtonGroup } from "../../../components/core/basic/ButtonGroup";
import { useQuery } from "@tanstack/react-query";
import api from "../../../utils/api";
import PieChart from "../../../components/core/blocks/PieChart";
import BarChart from "../../../components/core/blocks/BarChart";
import { trellisPalette } from "../../../components/custom/analytics/palettes";
import Loading from "../../../components/core/blocks/Loading";
import { Tabs, TabList, Tab, TabPanel } from "../../../components/core/basic/Tabs";
import { Modal } from "../../../components/core/basic/Modal";
import { useSimpleCan } from "../../../config/casl/casl";
import { useTranslation } from "react-i18next";
import { useDateFormatter } from "../../../i18n/useDateFormatter";
import KeywordCell from "../../../components/custom/category/advertisements/KeywordCell";
import { RT8ColumnSelector } from "../../../components/core/blocks/MetricSelector";
import { SearchInput } from "../../../components/fields/base/TextField";
import { createAppColumnHelper, useClientTable } from "../../../components/table/tableFeatures";
import { usePersistedTableState } from "../../../components/table/usePersistedTableState";
import { commaSearchFilterFn, customFilterFn } from "../../../components/table/filterFns";
import { resetTable } from "../../../components/table/utils";
import { RT8Pagination } from "../../../components/table/Pagination";
import { Table } from "../../../components/table/Table";
import { RT8FilterWidget } from "../../../modules/merchandising/components/tables/FilterWidget";

// Module-level so the query only rebuilds the list when the response changes.
const selectAudiences = (res) => res.data.results;

const getAudienceAsins = (audience) =>
  (audience.parameters.asins ?? audience.parameters.tracked_item_asins ?? []).filter((asin) => asin);

/**
 * The audience form's values for an existing audience, to show it read-only or to recreate it: the reverse of the
 * request AudienceForm builds on submit.
 */
const buildAudienceFormData = (audience, templates) => {
  const params = audience.parameters;
  // The form's Exact option is null; the API stores "EXACT".
  const lookalike = params.lookalikeAudienceExpectedReach;
  return {
    audience_name: audience.spec.audience_name ?? "",
    template_name: getTemplateSpecById(templates, audience.workflow_template)?.audience_template_name,
    asins: getAudienceAsins(audience),
    advertiser_id: audience.spec.advertiser_id,
    description: audience.spec.description ?? "",
    tags: audience.tags ?? [],
    refresh_rate_days: params.refreshRateDays,
    time_window_relative: params.timeWindowRelative ?? true,
    time_window_start: storedCalendarDay(params.timeWindowStart),
    time_window_end: latestSelectableAudienceDay(),
    lookalike_audience_expected_reach: lookalike && lookalike !== "EXACT" ? lookalike : null,
    views_range_lower: params.conversion_lower_bound ?? null,
    views_range_upper: params.conversion_upper_bound ?? null,
    search_term_fields: params.search_terms ?? [],
  };
};

const ADVERTISER_TYPES = [
  { value: "DISPLAY", label: "DSP" },
  { value: "SPONSORED_ADS", label: "Sponsored Ads" },
];

const formatTemplateTag = (tag) => (tag.toLowerCase() === "fsi" ? "FSI" : toTitleCase(tag));

const useInsights = (insightType, audienceId, marketPlace, enabled) => {
  return useQuery({
    queryKey: [audienceId, marketPlace, insightType],
    queryFn: async () => {
      return await api.post(`/dsp/insights/insights/`, {
        audience: [{ negative: "false", groupId: "Group 1", audienceId: audienceId }],
        marketplace: marketPlace === "USA" ? "US" : marketPlace,
        insights_type: insightType,
      });
    },
    enabled,
    select: (res) => res.data,
  });
};

const Expander = ({ audience }) => {
  const [tab, setTab] = useState("categories");
  const { marketPlace } = useAppSelector((state) => state.marketPlace);
  const { t } = useTranslation(["amc"]);
  const isDSP = audience.raw_amazon_body?.advertiserType === "DISPLAY";
  const hasInsights = isDSP && audience.status === "SUCCEEDED";
  const { templates } = useAudienceTemplates();
  const { data: demographicsData, isLoading } = useInsights(
    "demographics",
    audience.dsp_canonical_id,
    marketPlace,
    hasInsights,
  );
  const { data: categoriesData, isLoading: isLoadingCategories } = useInsights(
    "topCategoriesPurchased",
    audience.dsp_canonical_id,
    marketPlace,
    hasInsights,
  );

  const defaultFormData = useMemo(() => buildAudienceFormData(audience, templates), [audience, templates]);

  if (!isDSP) {
    return (
      <div
        className={css({
          padding: "15px",
          lineHeight: "150px",
          fontWeight: "bold",
          fontSize: "1.5rem",
          color: "gray.500",
          background: "gray.100",
          borderRadius: "15px",
          textAlign: "center",
          height: "150px",
        })}
      >
        {t(
          ($) =>
            $.amc["Demographic Overlap and Top Categories Purchased insights are only available for DSP audiences."],
        )}
      </div>
    );
  }

  return (
    <Tabs selectedKey={tab} onSelectionChange={setTab}>
      <TabList>
        <Tab id="categories">{t(($) => $.amc["Top Categories Purchased"])}</Tab>
        <Tab id="demographics">{t(($) => $.amc["Demographic Overlap"])}</Tab>
        <Tab id="configuration">{t(($) => $.amc["Configuration"])}</Tab>
      </TabList>
      <TabPanel id="categories">
        {isLoadingCategories && <Loading height={300} fullPage={false} />}
        {categoriesData?.retailCategories && (
          <BarChart
            title={t(($) => $.amc["Categories"])}
            metric={t(($) => $.amc["Overlap Percentage"])}
            colors={[trellisPalette[3]]}
            data={categoriesData.retailCategories}
            topN={20}
            hideOther
            accessor={(d) => d.insightMetrics.overlapPercentage}
            categoryAccessor={(d) => `${d.name}`}
            sortAccessor={(d) => d.insightMetrics.overlapPercentage}
            height={560}
          />
        )}
      </TabPanel>
      <TabPanel id="demographics">
        {isLoading && <Loading height={300} fullPage={false} />}
        {!demographicsData && !isLoading && (
          <div
            className={css({
              padding: "15px",
              lineHeight: "150px",
              fontWeight: "bold",
              fontSize: "1.5rem",
              color: "gray.500",
              background: "gray.100",
              borderRadius: "15px",
              textAlign: "center",
              height: "150px",
            })}
          >
            {t(($) => $.amc["No Insights Found. Insights can take Amazon 1-2 weeks to generate."])}
          </div>
        )}
        <Grid gap={3} columns={2}>
          {demographicsData?.demographics?.gender && (
            <PieChart
              title={t(($) => $.amc["Gender"])}
              metric={t(($) => $.amc["Gender"])}
              data={demographicsData.demographics?.gender}
              nameAccessor={(d) => d.attribute}
              accessor={(d) => d.insightMetrics.overlapPercentage}
            />
          )}
          {demographicsData?.demographics?.age && (
            <BarChart
              title={t(($) => $.amc["Age"])}
              metric={t(($) => $.amc["Overlap Percentage"])}
              colors={[trellisPalette[3]]}
              data={demographicsData.demographics?.age}
              accessor={(d) => d.insightMetrics.overlapPercentage}
              categoryAccessor={(d) => `${d.range.min} - ${d.range.max}`}
              sortAccessor={(d) => d.range.min * 1000 + d.range.max}
              topN={8}
              height={340}
            />
          )}
          {demographicsData?.demographics?.income && (
            <BarChart
              title={t(($) => $.amc["Income"])}
              metric={t(($) => $.amc["Overlap Percentage"])}
              colors={[trellisPalette[3]]}
              data={demographicsData.demographics?.income}
              accessor={(d) => d.insightMetrics.overlapPercentage}
              categoryAccessor={(d) => {
                if (!d.range.max) {
                  return `${formatCurrency(d.range.min, {
                    marketPlace: marketPlace,
                    decimalPlaces: 0,
                    compact: true,
                  })}+`;
                }
                return `${formatCurrency(d.range.min, {
                  marketPlace: marketPlace,
                  decimalPlaces: 0,
                  compact: true,
                })} - ${formatCurrency(d.range.max, {
                  marketPlace: marketPlace,
                  decimalPlaces: 0,
                  compact: true,
                })}`;
              }}
              sortAccessor={(d) => d.range.min}
              height={340}
            />
          )}
          {demographicsData?.demographics?.education && (
            <BarChart
              title={t(($) => $.amc["Education"])}
              metric={t(($) => $.amc["Overlap Percentage"])}
              colors={[trellisPalette[3]]}
              data={demographicsData.demographics?.education}
              accessor={(d) => d.insightMetrics.overlapPercentage}
              categoryAccessor={(d) => d.attribute}
              height={340}
            />
          )}
        </Grid>
      </TabPanel>
      <TabPanel id="configuration">
        <AudienceForm defaultFormData={defaultFormData} readOnly />
      </TabPanel>
    </Tabs>
  );
};

const columnHelper = createAppColumnHelper();

const canAlwaysExpand = () => true;

/**
 * Mounted once audiences, templates and advertisers have loaded: the columns read templates and advertisers,
 * and v9 keeps each row's values until `data` changes, so they must not arrive after the rows.
 */
const AudiencesTable = ({ audiences, isFailedView, templates, advertisersById, onRecreate, onShowFailureDetails }) => {
  const { t } = useTranslation(["amc", "common"]);
  const marketplaceLabel = useMarketplaceLabel();
  const dateFormatter = useDateFormatter("medium");
  const userCanCreateAmcAudiences = useSimpleCan("create", "AMCAudience");

  const { setters, state, initialState } = usePersistedTableState({
    persistKey: "amcAudiencesTable",
    initialState: {
      sorting: [{ id: "created_on", desc: true }],
      columnPinning: { start: ["spec_audience_name"], end: [] },
      pagination: { pageIndex: 0, pageSize: 5 },
      columnVisibility: {
        template_tags: false,
        audience_execution_id: false,
        dsp_audience_id: false,
        dsp_canonical_id: false,
        marketplace: false,
      },
    },
  });

  const getTemplate = useCallback(
    (audience) => getTemplateSpecById(templates, audience.workflow_template),
    [templates],
  );

  // The name from the AMC instance's advertiser list, else the raw id.
  const getAdvertiserName = useCallback(
    (audience) => {
      const advertiserId = audience.spec.advertiser_id;
      return advertiserId ? (advertisersById.get(advertiserId)?.name ?? advertiserId) : undefined;
    },
    [advertisersById],
  );

  const globalFilterFn = useMemo(
    () =>
      commaSearchFilterFn((audience) =>
        [
          audience.spec.audience_name,
          getTemplate(audience)?.audience_display_name,
          getAdvertiserName(audience),
          audience.spec.advertiser_id,
        ].map((field) => String(field ?? "")),
      ),
    [getTemplate, getAdvertiserName],
  );

  const columnFilterSelectOptions = useMemo(() => {
    const templateTags = [...new Set(templates.flatMap((template) => template.recommended_tags ?? []))].sort();
    const asins = [...new Set(audiences?.flatMap(getAudienceAsins))];
    return {
      status: [{ value: "all", label: t(($) => $.amc["All Statuses"]) }, ...AMC_AUDIENCE_STATUSES],
      raw_amazon_body_advertiserType: [
        { value: "all", label: t(($) => $.amc["All Advertiser Types"]) },
        ...ADVERTISER_TYPES,
      ],
      template_display_name: [
        { value: "all", label: t(($) => $.amc["All Types"]) },
        ...templates.map((template) => ({
          value: template.audience_display_name,
          label: template.audience_display_name,
        })),
      ],
      template_tags: [
        { value: "all", label: t(($) => $.amc["All Tags"]) },
        ...templateTags.map((tag) => ({ value: tag, label: formatTemplateTag(tag) })),
      ],
      asins: [
        { value: "all", label: t(($) => $.amc["All ASINs"]) },
        ...asins.map((asin) => ({ value: asin, label: asin })),
      ],
    };
  }, [audiences, templates, t]);

  const columns = useMemo(
    () => [
      columnHelper.accessor("spec.audience_name", {
        header: t(($) => $.amc["Audience"]),
        size: 240,
        enableHiding: false,
        sortFn: "alphanumeric",
        sortUndefined: "last",
        enableColumnFilter: false,
        cell: ({ getValue }) => <span className={css({ fontWeight: 500 })}>{getValue()}</span>,
      }),
      columnHelper.accessor("status", {
        header: t(($) => $.amc["Status"]),
        size: 110,
        enableResizing: false,
        sortFn: "alphanumeric",
        sortUndefined: "last",
        cell: ({ getValue }) => toTitleCase(getValue()),
        meta: {
          columnSelectorOptions: { description: t(($) => $.amc["Whether Amazon finished building the audience."]) },
          columnFilterOptions: { format: "single-select" },
        },
      }),
      columnHelper.accessor((audience) => getTemplate(audience)?.audience_display_name ?? undefined, {
        id: "template_display_name",
        header: t(($) => $.amc["Type"]),
        size: 200,
        sortFn: "alphanumeric",
        sortUndefined: "last",
        meta: {
          columnSelectorOptions: {
            description: t(($) => $.amc["The audience template this audience was built from."]),
          },
          columnFilterOptions: { format: "single-select", label: t(($) => $.amc["Audience Type"]) },
        },
      }),
      columnHelper.accessor((audience) => getTemplate(audience)?.recommended_tags ?? [], {
        id: "template_tags",
        header: t(($) => $.amc["Tags"]),
        size: 180,
        enableSorting: false,
        cell: ({ getValue }) => listFormatter.format(getValue().map(formatTemplateTag)),
        meta: { columnFilterOptions: { format: "multi-select" } },
      }),
      columnHelper.accessor("raw_amazon_body.advertiserType", {
        header: t(($) => $.amc["Advertiser Type"]),
        size: 150,
        enableResizing: false,
        sortFn: "alphanumeric",
        sortUndefined: "last",
        cell: ({ getValue }) => ADVERTISER_TYPES.find((type) => type.value === getValue())?.label ?? getValue(),
        meta: {
          columnSelectorOptions: {
            description: t(($) => $.amc["Whether the audience belongs to a Sponsored Ads entity or a DSP advertiser."]),
          },
          columnFilterOptions: { format: "single-select" },
        },
      }),
      columnHelper.accessor(getAdvertiserName, {
        id: "advertiser",
        header: t(($) => $.amc["Advertiser"]),
        size: 180,
        sortFn: "alphanumeric",
        sortUndefined: "last",
        cell: ({ getValue, row }) => {
          const advertiser = getValue();
          const advertiserId = row.original.spec.advertiser_id;

          if (!advertiserId) {
            return <span className="text-muted">{"-"}</span>;
          }

          const marketplaceId = advertisersById.get(advertiserId)?.marketplaceId;
          const countryCode = marketplaceId ? MARKET_PLACE_ID_TO_COUNTRY_CODE_MAP[marketplaceId] : null;
          // When the advertiser lookup misses, `advertiser` is already the raw id, so the tooltip would just repeat it.
          const tooltip = [
            advertiser === advertiserId ? null : advertiser,
            advertiserId,
            countryCode ? marketplaceLabel(countryCode) : null,
          ]
            .filter(Boolean)
            .join(" · ");

          return (
            <CustomTriggerTooltip toolTip={tooltip} placement="top">
              <span
                className={css({
                  cursor: "help",
                  display: "block",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                })}
              >
                {advertiser}
              </span>
            </CustomTriggerTooltip>
          );
        },
        meta: {
          columnSelectorOptions: {
            description: t(($) => $.amc["The advertiser account this audience was created for."]),
          },
        },
      }),
      columnHelper.accessor(getAudienceAsins, {
        id: "asins",
        header: t(($) => $.amc["ASINs"]),
        size: 180,
        enableSorting: false,
        cell: ({ getValue }) => {
          const asins = getValue();
          return (
            <OverlayTrigger
              overlay={
                <Tooltip
                  placement="top"
                  className="in text-center"
                  id="tooltip-top"
                  bsPrefix="tr-popover-alt bg-purple"
                  style={{ maxWidth: "50%" }}
                >
                  <span className="text-center mx-auto fs-5">
                    {listFormatter.format(asins)} <br />
                    <span className={css({ textDecoration: "underline", display: "block" })}>
                      {t(($) => $.amc["Click to copy ASINs to clipboard"])}
                    </span>
                  </span>
                </Tooltip>
              }
            >
              <span
                className={css({ cursor: "pointer", overflow: "hidden", lineClamp: 4, WebkitLineClamp: 4 })}
                onClick={() => {
                  navigator.clipboard.writeText(asins.join(","));
                  successToast({ message: t(($) => $.amc["ASINs copied to clipboard"]) });
                }}
              >
                {listFormatter.format(asins)}
              </span>
            </OverlayTrigger>
          );
        },
        meta: {
          columnSelectorOptions: {
            description: t(
              ($) => $.amc["The ASINs the audience was built from. Click the cell to copy the full list."],
            ),
          },
          columnFilterOptions: { format: "multi-select" },
        },
      }),
      columnHelper.accessor("raw_amazon_body.audienceCount", {
        header: t(($) => $.amc["Audience Count"]),
        size: 120,
        enableResizing: false,
        sortUndefined: "last",
        cell: ({ getValue }) => formatNumber(getValue()),
        meta: {
          justify: "end",
          columnSelectorOptions: {
            description: t(($) => $.amc["The number of shoppers in the audience as of its last refresh."]),
          },
          columnFilterOptions: { format: "number" },
        },
      }),
      // The ids are null until Amazon assigns them, and sortUndefined only moves undefined to the end.
      columnHelper.accessor((audience) => audience.audience_execution_id ?? undefined, {
        id: "audience_execution_id",
        header: t(($) => $.amc["Execution ID"]),
        size: 260,
        sortFn: "alphanumeric",
        sortUndefined: "last",
        cell: ({ getValue }) => <KeywordCell value={getValue()} />,
        meta: {
          columnSelectorOptions: {
            description: t(
              ($) => $.amc["Identifier for the AMC run that built this audience. Quote it when contacting support."],
            ),
          },
        },
      }),
      columnHelper.accessor((audience) => audience.dsp_audience_id ?? undefined, {
        id: "dsp_audience_id",
        header: t(($) => $.amc["Audience ID"]),
        size: 150,
        sortFn: "alphanumeric",
        sortUndefined: "last",
        cell: ({ getValue }) => <KeywordCell value={getValue()} />,
        meta: {
          columnSelectorOptions: {
            description: t(($) => $.amc["The audience's identifier in Amazon DSP, used to target it in a campaign."]),
          },
        },
      }),
      columnHelper.accessor((audience) => audience.dsp_canonical_id ?? undefined, {
        id: "dsp_canonical_id",
        header: t(($) => $.amc["Canonical ID"]),
        size: 240,
        sortFn: "alphanumeric",
        sortUndefined: "last",
        cell: ({ getValue }) => <KeywordCell value={getValue()} />,
        meta: {
          columnSelectorOptions: { description: t(($) => $.amc["Amazon's canonical identifier for the audience."]) },
        },
      }),
      // Only failed audiences have a failure reason.
      ...(isFailedView ?
        [
          columnHelper.accessor("audience_execution_status_reason", {
            header: t(($) => $.amc["Details"]),
            size: 80,
            enableResizing: false,
            enableSorting: false,
            enableColumnFilter: false,
            cell: ({ getValue }) => {
              const reason = getValue();
              if (!reason) {
                return null;
              }
              return (
                <OverlayTrigger
                  overlay={
                    <Tooltip
                      placement="top"
                      className="in text-center"
                      id="tooltip-top"
                      bsPrefix="tr-popover-alt bg-purple"
                      style={{ maxWidth: "50%" }}
                    >
                      <div className="text-center mx-auto fs-5">{reason}</div>
                    </Tooltip>
                  }
                >
                  <span className={css({ color: "grey", cursor: "pointer" })}>
                    <FaInfoCircle onClick={() => onShowFailureDetails(reason)} />
                  </span>
                </OverlayTrigger>
              );
            },
            meta: {
              justify: "center",
              columnSelectorOptions: {
                description: t(($) => $.amc["The reason Amazon gave for a failed audience."]),
              },
            },
          }),
        ]
      : []),
      columnHelper.accessor("marketplace", {
        header: t(($) => $.amc["Marketplace"]),
        size: 120,
        enableResizing: false,
        sortFn: "alphanumeric",
        sortUndefined: "last",
        meta: {
          columnSelectorOptions: {
            description: t(($) => $.amc["The Amazon marketplace the audience was created in."]),
          },
        },
      }),
      columnHelper.accessor("created_on", {
        header: t(($) => $.amc["Created On"]),
        size: 130,
        enableResizing: false,
        sortUndefined: "last",
        cell: ({ getValue }) => (getValue() ? dateFormatter.format(new Date(getValue())) : "-"),
        meta: {
          columnSelectorOptions: { description: t(($) => $.amc["When the audience was created."]) },
          columnFilterOptions: { format: "date" },
        },
      }),
      columnHelper.display({
        id: "actions",
        header: t(($) => $.amc["Actions"]),
        size: 80,
        enableResizing: false,
        cell: ({ row }) => (
          <OverlayTrigger
            overlay={
              <Tooltip
                placement="top"
                className="in text-center"
                bsPrefix="tr-popover-alt bg-purple"
                style={{ maxWidth: "50%" }}
              >
                <span className="text-center mx-auto fs-5">
                  {t(($) => $.amc["Recreate audience with updated parameters"])}
                </span>
              </Tooltip>
            }
          >
            <span className={css({ color: "grey" })}>
              <FaClone
                onClick={() => {
                  if (userCanCreateAmcAudiences) {
                    onRecreate(row.original);
                  }
                }}
                className={css({ cursor: userCanCreateAmcAudiences ? "pointer" : "not-allowed" })}
                aria-disabled={!userCanCreateAmcAudiences}
              />
            </span>
          </OverlayTrigger>
        ),
        meta: {
          justify: "center",
          columnSelectorOptions: {
            description: t(($) => $.amc["Recreate this audience with updated parameters."]),
          },
        },
      }),
    ],
    [
      t,
      getTemplate,
      getAdvertiserName,
      advertisersById,
      marketplaceLabel,
      dateFormatter,
      isFailedView,
      userCanCreateAmcAudiences,
      onRecreate,
      onShowFailureDetails,
    ],
  );

  const table = useClientTable({
    data: audiences,
    columns,
    getRowId: (audience) => String(audience.id),
    // Insights and the configuration are for audiences Amazon built, so failed ones don't expand.
    getRowCanExpand: isFailedView ? undefined : canAlwaysExpand,
    defaultColumn: { filterFn: customFilterFn },
    globalFilterFn,
    initialState,
    state,
    ...setters,
    meta: { columnFilterSelectOptions },
  });

  return (
    <>
      <HStack padding={"small"} fontSize={"large"}>
        <VStack flex="1" alignItems={"start"}>
          <HStack width={"100%"}>
            <Button
              variant={"contentOnly"}
              aria-label={t(($) => $.common.table["Reset table filters"])}
              onPress={() => resetTable(table)}
              fontSize={"xxlarge"}
            >
              <MdRotateLeft />
            </Button>
            <RT8ColumnSelector table={table} />
            <SearchInput
              aria-label={t(($) => $.common.actions["Search"])}
              isExpandable
              css={{ flex: "0 1 350px" }}
              value={table.state.globalFilter ?? ""}
              onChange={table.setGlobalFilter}
            />
          </HStack>
          <RT8FilterWidget table={table} />
        </VStack>
        <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
      </HStack>
      <Table table={table} renderExpander={(row) => <Expander audience={row.original} />} />
    </>
  );
};

const AMCAudienceDetails = () => {
  const [showAudienceForm, setShowAudienceForm] = useState(false);
  const [defaultFormData, setDefaultFormData] = useState({});
  const [audienceDetailsModalText, setAudienceDetailsModalText] = useState(null);

  const marketPlace = useAppSelector((state) => state.marketPlace);
  const [status, setStatus] = useQueryParams("status", "active");
  const userCanCreateAmcAudiences = useSimpleCan("create", "AMCAudience");
  const { t } = useTranslation(["amc"]);

  const { templates, isLoading: isLoadingTemplates } = useAudienceTemplates();
  const { byId: amcAdvertisersById, isLoading: isLoadingAdvertisers } = useAmcAdvertisers();

  const { data, isLoading } = useFetch(
    ["amcAudienceSpecs", marketPlace.marketPlace],
    "/amc/audiencespec/",
    { ordering: "-created_on" },
    { select: selectAudiences },
  );

  // Active and failed audiences are separate lists, so switching starts on page one with nothing expanded.
  const audiences = useMemo(() => {
    return data?.filter((audience) => {
      return status === "active" ? audience.status !== "FAILED" : audience.status === "FAILED";
    });
  }, [data, status]);

  const recreateAudience = useCallback(
    (audience) => {
      setDefaultFormData(buildAudienceFormData(audience, templates));
      setShowAudienceForm(true);
    },
    [templates],
  );

  return (
    <Row className="pb-5">
      <Col xs={12}>
        <h3 className="pb-3 ps-5 fs-2" style={{ fontWeight: 600 }}>
          {t(($) => $.amc["Audiences"])}
        </h3>
        <Modal
          isOpen={showAudienceForm}
          onOpenChange={(isOpen) => setShowAudienceForm(isOpen)}
          css={{
            width: "60vw",
            padding: "xlarge",
          }}
        >
          <AudienceForm setShowAudienceForm={setShowAudienceForm} defaultFormData={defaultFormData} />
        </Modal>
        <Modal
          isOpen={audienceDetailsModalText}
          onOpenChange={(isOpen) => {
            if (!isOpen) {
              setAudienceDetailsModalText(null);
            }
          }}
          css={{
            width: "60vw",
            padding: "xlarge",
          }}
          title={t(($) => $.amc["Audience Failure Details"])}
        >
          <Flex flexDirection={"column"} gap={"large"} alignItems={"center"}>
            <p className="fs-4 text-center">{audienceDetailsModalText}</p>
            <Button onPress={() => setAudienceDetailsModalText(null)}>{t(($) => $.amc["Continue"])}</Button>
          </Flex>
        </Modal>
        <Card>
          <div className="pb-5 w-100">
            <Flex justify={"space-between"} wrap={"wrap"} gap="small" alignContent={"center"} paddingBottom={"medium"}>
              <ButtonGroup
                buttons={[
                  {
                    label: t(($) => $.amc["Active"]),
                    value: "active",
                  },
                  { label: t(($) => $.amc["Failed"]), value: "failed" },
                ]}
                value={status}
                onSelect={setStatus}
              />
            </Flex>
            {isLoading || isLoadingTemplates || isLoadingAdvertisers ?
              <Loading height={300} fullPage={false} />
            : <AudiencesTable
                audiences={audiences}
                isFailedView={status === "failed"}
                templates={templates}
                advertisersById={amcAdvertisersById}
                onRecreate={recreateAudience}
                onShowFailureDetails={setAudienceDetailsModalText}
              />
            }
            {!data?.length && !isLoading ?
              <div className="d-flex flex-column gap-3 align-items-center justify-content-center pt-5 pb-3">
                <p className="fs-4">
                  {t(
                    ($) =>
                      $.amc[
                        "No audiences have been created yet. Create your first audience by clicking the button below."
                      ],
                  )}
                </p>
                <Button
                  onPress={() => {
                    setShowAudienceForm(true);
                  }}
                  isDisabled={!userCanCreateAmcAudiences}
                >
                  {t(($) => $.amc["Create a New Audience"])}
                </Button>
              </div>
            : null}
          </div>
        </Card>
      </Col>
    </Row>
  );
};

export default AMCAudienceDetails;
